import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import './styles/EnrolFooter.scss';

import { classNames, formatCurrency, formatFullClassName, mapSelectedClassFees, getTermForDate } from '../utils';
import { Button, Select } from './core';
import { IDanceClass, SCREEN_SIZE, ISelectedClassFee, IDanceSchool, IOptionsItem } from '../models';
import { TermsAndConditionsFooter } from './TermsAndConditionsFooter';
import dayjs from 'dayjs';
import { DATE_FORMATS } from '../constants';

interface IEnrolFooterProps {
  danceSchool: IDanceSchool;
  selectedClasses: IDanceClass[];
  screenSize: SCREEN_SIZE;
  disabled?: boolean;
  isError?: boolean;
  onSelectedEnrolmentStartDateChanged: (date: string) => void;
}
export const EnrolFooter: FunctionComponent<IEnrolFooterProps> = (props: IEnrolFooterProps) => {

  const {
    danceSchool,
    selectedClasses,
    screenSize,
    disabled,
    isError,
    onSelectedEnrolmentStartDateChanged
  } = props;

  const [selectedEnrolmentStartDate, setSelectedEnrolmentStartDate] = useState<string>('');

  const selectedClassFees = useMemo<ISelectedClassFee[]>(() => mapSelectedClassFees(selectedClasses, danceSchool.countryCode), [selectedClasses, danceSchool]);
  const totalSelectedClassesFees = useMemo(() => {
    return selectedClassFees.filter(x => !x.noCharge).reduce((total, item) => total += item.fee, 0);
  }, [selectedClassFees]);

  const enrolmentStartDateOptions = useMemo<IOptionsItem[]>(() => {
    const currentExactTerm = getTermForDate(dayjs(), danceSchool, true);
    if (!currentExactTerm) {
      // We are between terms most likely, so don't even show the enrolment start date options
      return [];
    } else if (currentExactTerm.termNumber >= danceSchool.latestEnrolmentYearSettings.termDates.length) {
      // We are in the last term, so don't show any options
      return [];
    }

    const nextTerm = danceSchool.latestEnrolmentYearSettings.termDates[currentExactTerm.termNumber];
    return [
      {
        label: 'As Soon As Possible',
        value: dayjs().format(DATE_FORMATS.YearMonthDayIso)
      },
      {
        label: `Term ${currentExactTerm.termNumber + 1} (${nextTerm.startDate.format(DATE_FORMATS.DayMonthYear)})`,
        value: nextTerm.startDate.format(DATE_FORMATS.YearMonthDayIso)
      }
    ];
  }, [danceSchool]);

  useEffect(() => {
    setSelectedEnrolmentStartDate(dayjs().format(DATE_FORMATS.YearMonthDayIso));
  }, []);
  useEffect(() => {
    onSelectedEnrolmentStartDateChanged(selectedEnrolmentStartDate ?? '');
  }, [selectedEnrolmentStartDate, onSelectedEnrolmentStartDateChanged]);

  return (
    <div className={classNames('ppa-schedule-enrol-footer', screenSize)}>
      {[SCREEN_SIZE.LARGE, SCREEN_SIZE.EXTRA_LARGE].includes(screenSize) && (
        <div className='footer-column'></div>
      )}

      <div className='footer-column'>
        {selectedClassFees.length > 0 && (
          <div className={classNames('footer-fees-breakdown', {
            'no-border': enrolmentStartDateOptions.length > 0
          })}>
            {selectedClassFees.map((x, index) => (
              <div key={index} className='fee-breakdown-row'>
                <div className='description'>
                  {formatFullClassName(x.classDetails)}
                </div>
                <div className='fee'>
                  <label className={classNames({ 'no-charge': x.noCharge })}>
                    {formatCurrency(x.fee, danceSchool.countryCode)}
                  </label>
                  {x.noCharge && (
                    <label>FREE!</label>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {enrolmentStartDateOptions.length > 0 && (
          <div className='footer-enrolment-start-date'>
            <div className='description'>
              Enrolment Start Date
            </div>
            <div className='date-options-wrapper'>
              <Select
                label='Start Date'
                options={enrolmentStartDateOptions}
                onChange={(option) => setSelectedEnrolmentStartDate(option?.value ?? '')}
                value={selectedEnrolmentStartDate ?? ''}
                required
                disabled={disabled}
              />
            </div>
          </div>
        )}

        {selectedClassFees.length > 0 && (
          <TermsAndConditionsFooter danceSchool={danceSchool} disabled={disabled} />
        )}

        <div className='footer-actions'>
          <h3
            style={{
              opacity: selectedClassFees.length < 1 ? 0.6 : 1
            }}
          >
            Total Per Term: {formatCurrency(totalSelectedClassesFees, danceSchool.countryCode)}
          </h3>
          
          <div className='form-submit-wrapper'>
            <Button
              type='submit'
              disabled={selectedClassFees.length < 1}
              loading={disabled}
            >
              Enrol in {selectedClassFees.length} class{selectedClassFees.length !== 1 ? 'es' : ''}
            </Button>

            {isError && (
              <div className='error-label'>Something went wrong, please try again.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}